import { Tab, Tabs, useMediaQuery } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-use';

import Icon from 'atoms/Icons/Icon';
import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';
import { font } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';
import TestimonialSlider from 'components/TabbedSwitcher/TestimonialSlider';

// Scripts
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentTabbedSwitcher, ContentfulMenuSwitcherItem } from 'graphqlTypes';

interface TabbedSwitcherProps extends FlexProps, ContentfulComponentTabbedSwitcher {
  backgroundIsDark: boolean;
}

interface TabPanelProps {
  children?: ContentfulMenuSwitcherItem;
  index: number;
  value: number;
}

const TabbedSwitcherV1: FC<TabbedSwitcherProps> = ({ switcherItem, backgroundIsDark, switcherHeading, ...props }) => {
  const [active, setActive] = useState(0);
  const isDesktop = useMediaQuery(media.lg);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const toggleParam = hash.split('toggle=')[1];
      const formattedQueryParam = toggleParam?.replaceAll('-', ' ').replace('#', '');

      const index = switcherItem?.findIndex(({ title }) => {
        const lowercaseTitle = title?.toLowerCase();

        return lowercaseTitle === formattedQueryParam;
      });

      if (index && index > -1) {
        setActive(index);
      }

      const id = hash?.substring(hash.indexOf('#'), hash.lastIndexOf('?'));

      if (id?.startsWith('#')) {
        const elem = document.querySelector(id);

        window.scrollTo({
          top: elem.getBoundingClientRect().top + window.scrollY - 50,
          behavior: 'smooth',
        });
      }
    }
  }, [hash]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActive(newValue);
  };

  const TabPanel = (propsItem: TabPanelProps) => {
    const { children, value, index } = propsItem;
    if (children) {
      const { listItem, callToAction, reference } = children;
      if (listItem) {
        return (
          <Flex
            role="tabpanel"
            hidden={value !== index}
            width="100%"
            flexDirection="column"
            marginTop={0}
            display={value !== index ? 'none' : 'flex'}
            lg={{ marginTop: 32 }}
          >
            {value === index && (
              <Flex flexDirection="column" gap="60px" lg={{ gap: '110px', flexDirection: 'row' }}>
                <Flex flexDirection="column" gap="40px" width="100%" maxWidth="100%">
                  <Flex flexDirection="column" gap="32px" width="100%" textColor="common-white">
                    {listItem &&
                      listItem.map(item => (
                        <Flex
                          flexDirection="row"
                          gap="18px"
                          alignItems="flex-start"
                          key={item?.id || 'List Item'}
                          sm={{ flexDirection: 'row' }}
                        >
                          <Container minWidth="20px" maxWidth="20px" minHeight="20px" maxHeight="20px">
                            <Icon id="check" fill={backgroundIsDark ? 'white' : 'black'} size={24} />
                          </Container>

                          <Flex flexDirection="column" gap="8px">
                            {item?.heading && (
                              <Text
                                textStyle="textMd"
                                fontWeight="bold"
                                textColor={backgroundIsDark ? 'white' : 'black'}
                              >
                                {item.heading}
                              </Text>
                            )}
                            {item?.body && (
                              <Flex
                                flexDirection="column"
                                gap="16px"
                                fontSize="textMd"
                                textColor={backgroundIsDark ? 'white' : 'black'}
                              >
                                {richTextParser(item.body)}
                              </Flex>
                            )}
                          </Flex>
                        </Flex>
                      ))}
                  </Flex>
                  {callToAction && (
                    <Flex gap="4px" width="100%">
                      <ComponentButton
                        {...callToAction}
                        buttonColor={backgroundIsDark ? 'Outline White' : 'Outline Black'}
                        css={{ paddingAll: 0, maxWidth: '100% !important', width: '100% !important' }}
                      />
                    </Flex>
                  )}
                </Flex>
                {reference?.__typename === 'ContentfulComponentImage' && (
                  <Flex width="100%">
                    <Image
                      image={(reference?.bynderImageDesktop || reference?.imageDesktop) as ImageData}
                      alt={reference?.imageDesktop?.title || 'List Item Featured Image'}
                      boxShadow={reference?.boxShadow}
                      height="100%"
                      width="100%"
                      objectFit="contain"
                      objectPosition="center"
                      roundedCorners={reference?.roundedCorners}
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        );
      }
      if (reference?.__typename === 'ContentfulComponentTestimonialSlider') {
        return (
          <Flex
            role="tabpanel"
            hidden={value !== index}
            width="100%"
            flexDirection="column"
            display={value !== index ? 'none' : 'flex'}
          >
            <TestimonialSlider backgroundIsDark={backgroundIsDark} {...reference} cIndex={index} />
          </Flex>
        );
      }
    }

    return <Flex>No Tabs</Flex>;
  };

  return (
    <Flex
      flexDirection="column"
      gap="32px"
      width="100%"
      maxWidth="100%"
      marginX="auto"
      md={{ maxWidth: '770px' }}
      lg={{
        maxWidth:
          switcherItem?.[0]?.reference?.__typename === 'ContentfulComponentTestimonialSlider' ? '770px' : '100%',
      }}
      {...props}
    >
      <Flex
        gap="24px"
        flexDirection="column"
        textAlign="center"
        alignItems="center"
        width="100%"
        maxWidth="770px"
        marginX="auto"
        css={`
          padding-bottom: 1rem;

          ${media.xs} {
            padding-top: 1rem;
            padding-bottom: 2rem;
          }

          ${media.md} {
            padding-top: 3rem;
          }

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            ${media.xs} {
              ${font.display.sm}
              color: ${backgroundIsDark ? color.common.white : color.common.black}
            }
            ${media.lg} {
              ${font.display.lg}
              color: ${backgroundIsDark ? color.common.white : color.common.black}
            }
          }

          p {
            ${font.text.lg}
            color: ${backgroundIsDark ? color.common.white : color.common.black}
          }
        `}
      >
        {switcherHeading && richTextParser(switcherHeading)}
      </Flex>
      {switcherItem && (
        <Flex
          flexDirection="column"
          width="100%"
          height="100%"
          position="relative"
          paddingTop={96}
          md={{ paddingTop: 0 }}
        >
          <Tabs
            value={active}
            onChange={handleChange}
            variant={isDesktop ? 'fullWidth' : 'scrollable'}
            scrollButtons={true}
            allowScrollButtonsMobile
            sx={{
              '&& .MuiTabScrollButton-root': {
                display: 'none',
              },
              '&& .MuiTabs-flexContainer': {
                gap: '20px',
                justifyContent: 'space-between',
                [media.md]: {
                  gap: 0,
                },
              },
              '&& .MuiButtonBase-root': {
                color: backgroundIsDark ? color.common.white : color.common.black,
                fontSize: '14px',
                fontWeight: 'bold',
                minHeight: 'auto',
                padding: '0 2px 1rem 2px',
                textTransform: 'inherit',
                textAlign: 'center',
                alignItems: 'center',
                borderBottom: `2px solid ${backgroundIsDark ? color.navy[700] : color.gray[400]}`,
                maxWidth: 'fit-content',
                justifyContent: 'space-between',
                flexDirection: 'column',
                gap: '0px',
                [media.sm]: {
                  justifyContent: 'center',
                  flexDirection: 'row',
                  gap: '16px',
                },
                [media.md]: {
                  fontSize: '18px',
                  padding: '0 10px 1rem 10px',
                },
                [media.lg]: {
                  fontSize: '18px',
                  maxWidth: 'inherit',
                },
              },
              '&& .MuiTabScrollButton-root svg': {
                width: '1.5rem',
                height: '1.5rem',
              },
              '&& .MuiTabs-indicator': {
                backgroundColor: backgroundIsDark ? color.common.white : color.common.black,
              },
              '&& .MuiTab-iconWrapper': {
                marginBottom: '6px',
                [media.md]: {
                  marginBottom: 0,
                },
              },
            }}
            css={`
              padding: 0 20px;

              && .MuiTabs-flexContainer {
                &::-webkit-scrollbar {
                  display: none;
                }
              }

              @media (max-width: 768px) {
                position: absolute;
                margin-left: -20px;
                width: 100vw;
                top: -10px;
              }

              @media (max-width: 575px) {
                .MuiTabScrollButton-root:first-child {
                  max-width: 30px !important;
                  padding-right: 0 !important;
                }

                .MuiTabScrollButton-root:last-child {
                  max-width: 30px !important;
                  padding-left: 0 !important;
                }
              }
            `}
          >
            {switcherItem.map((tabItem, idx) => (
              <Tab
                tabIndex={idx}
                key={`${tabItem?.title} - tab`}
                label={tabItem?.title}
                icon={
                  <Container
                    css={`
                      marginbottom: 5px;
                      img {
                        filter: ${backgroundIsDark ? 'grayscale(100%) invert(100%)' : 'grayscale(100%) invert(0%)'};
                      }
                    `}
                  >
                    <Image
                      image={tabItem?.icon as ImageData}
                      alt={tabItem?.icon?.title || 'TabbedSwitcher Item Asset'}
                      height="30px"
                      width="30px"
                      md={{ height: '40px', width: '40px' }}
                    />
                  </Container>
                }
                css={`
                  opacity: ${backgroundIsDark ? 1 : 0.6};
                `}
                sx={{
                  '&&.Mui-selected': {
                    color: backgroundIsDark ? color.common.white : color.common.black,
                    opacity: 1,
                  },
                }}
              />
            ))}
          </Tabs>
          <Flex flexDirection="column" paddingTop={16} gap="32px">
            {switcherItem &&
              switcherItem.map((tabItem, idx) => (
                <TabPanel key={`tabpanel - ${tabItem?.title}`} index={idx} value={active}>
                  {tabItem || undefined}
                </TabPanel>
              ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default TabbedSwitcherV1;
